import React, { useState, useEffect, useCallback } from 'react';
import { Card, DatePicker, Row, Col, Statistic, Button, Alert, message, Spin, Empty, Space } from 'antd';
import { financeService, ProfitSummary, ProfitTrendPoint, SKUCostConfig } from '../../services/financeService';
import dayjs from 'dayjs';
import ReactECharts from 'echarts-for-react';
import { CostConfigModal } from '../Finance/CostConfigModal';
import { SettingOutlined, DollarOutlined, ReloadOutlined } from '@ant-design/icons';

const { RangePicker } = DatePicker;

interface Props {
    skuId: number;
}

export const SKUProfitAnalysis: React.FC<Props> = ({ skuId }) => {
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState<ProfitSummary | null>(null);
    const [trendData, setTrendData] = useState<ProfitTrendPoint[]>([]);
    const [costConfig, setCostConfig] = useState<SKUCostConfig | null>(null);
    const [configLoading, setConfigLoading] = useState(false);

    // modal state
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
        dayjs().subtract(30, 'days'),
        dayjs(),
    ]);

    const loadConfig = useCallback(async () => {
        setConfigLoading(true);
        try {
            const res = await financeService.getCostConfigs({ seller_sku: skuId });
            if (res.data.results && res.data.results.length > 0) {
                setCostConfig(res.data.results[0]);
            } else {
                setCostConfig(null);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setConfigLoading(false);
        }
    }, [skuId]);

    const loadData = useCallback(async () => {
        if (!skuId) return;
        setLoading(true);
        try {
            const params = {
                start_date: dateRange[0].format('YYYY-MM-DD'),
                end_date: dateRange[1].format('YYYY-MM-DD'),
                seller_sku: skuId
            };

            const [summaryRes, trendRes] = await Promise.all([
                financeService.getProfitSummary(params),
                financeService.getProfitTrend({ ...params, granularity: 'day' })
            ]);

            setSummary(summaryRes.data);
            setTrendData(trendRes.data);
        } catch (error) {
            console.error('Fetch profit data failed', error);
            message.error('加载利润数据失败');
        } finally {
            setLoading(false);
        }
    }, [skuId, dateRange]);

    useEffect(() => {
        loadConfig();
        loadData();
    }, [loadConfig, loadData]);

    const getOption = () => {
        return {
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
            legend: { data: ['销售额', '毛利润', '销量'] },
            grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
            xAxis: {
                type: 'category',
                data: trendData.map(item => dayjs(item.period).format('MM-DD')),
            },
            yAxis: [
                { type: 'value', name: '金额' },
                { type: 'value', name: '销量', splitLine: { show: false } }
            ],
            series: [
                {
                    name: '销售额',
                    type: 'bar',
                    data: trendData.map(item => item.revenue),
                    itemStyle: { color: '#1890ff' }
                },
                {
                    name: '毛利润',
                    type: 'bar',
                    data: trendData.map(item => item.profit),
                    itemStyle: { color: '#52c41a' }
                },
                {
                    name: '销量',
                    type: 'line',
                    yAxisIndex: 1,
                    data: trendData.map(item => item.units),
                    itemStyle: { color: '#faad14' }
                }
            ]
        };
    };

    return (
        <div style={{ padding: '0 8px' }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                    <RangePicker
                        value={dateRange}
                        onChange={(dates) => dates && setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
                        allowClear={false}
                    />
                    <Button icon={<ReloadOutlined />} onClick={loadData}>刷新</Button>
                </Space>
                <Button
                    icon={<SettingOutlined />}
                    onClick={() => setIsModalOpen(true)}
                >
                    {costConfig ? '修改成本配置' : '设置成本配置'}
                </Button>
            </div>

            {!costConfig && !configLoading && (
                <Alert
                    message="未检测到成本配置"
                    description="请点击右上角'设置成本配置'完善成本信息，否则利润数据可能不准确（显示为0）。"
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={6}>
                    <Card size="small">
                        <Statistic
                            title="总销售额"
                            value={summary?.total_revenue}
                            precision={2}
                            prefix="$"
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small">
                        <Statistic
                            title="毛利润"
                            value={summary?.total_profit}
                            precision={2}
                            valueStyle={{ color: (summary?.total_profit || 0) >= 0 ? '#3f8600' : '#cf1322' }}
                            prefix="$"
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small">
                        <Statistic
                            title="利润率"
                            value={summary?.avg_profit_margin}
                            precision={1}
                            suffix="%"
                            valueStyle={{ color: (summary?.avg_profit_margin || 0) >= 15 ? '#3f8600' : '#cf1322' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small">
                        <Statistic
                            title="销量"
                            value={summary?.total_units}
                            prefix={<DollarOutlined />}
                        />
                    </Card>
                </Col>
            </Row>

            <Card title="利润趋势" bordered={false} loading={loading}>
                {trendData.length > 0 ? (
                    <ReactECharts option={getOption()} style={{ height: 350 }} />
                ) : (
                    <Empty description="暂无数据" />
                )}
            </Card>

            <CostConfigModal
                visible={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                onSuccess={() => {
                    setIsModalOpen(false);
                    loadConfig(); // Reload config
                    loadData(); // Reload data (profit might change if recalculated, though recalculate must be triggered manually or server side)
                    // Note: changing cost config doesn't auto-recalculate existing reports.
                    // Ideally we should trigger recalculate or user should.
                    // For now, let's just reload.
                    message.info('成本已更新，请前往利润大盘或使用自动重算功能更新历史报表。');
                }}
                editingItem={costConfig}
                fixedSkuId={skuId}
            />
        </div>
    );
};
